// Coach tab: streaming markdown chat + quick-action chips. The streaming tail
// is rendered as plain text until a paragraph closes (blank line), which keeps
// react-markdown from re-flowing half-written constructs.

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ChatMessage } from '../../shared/types.ts';
import { analysisAvailability } from '../availability.ts';
import { setQuery } from '../router.ts';
import { lastHumanBoardMove, useStore } from '../store/useStore.ts';

const TOOL_LABEL: Record<string, string> = {
  request_katago_analysis: 'Consulting KataGo…',
  get_move_grades: 'Fetching move grades…',
};

function splitStreamTail(text: string): { stable: string; tail: string } {
  const idx = text.lastIndexOf('\n\n');
  if (idx < 0) return { stable: '', tail: text };
  return { stable: text.slice(0, idx), tail: text.slice(idx + 2) };
}

function Message({ msg }: { msg: ChatMessage }) {
  if (msg.role === 'user') {
    return <div className="chat-msg chat-user">{msg.content}</div>;
  }
  return (
    <div className="chat-msg chat-assistant">
      <ReactMarkdown>{msg.content}</ReactMarkdown>
    </div>
  );
}

export function CoachChat() {
  const detail = useStore((s) => s.detail);
  const cursor = useStore((s) => s.cursor);
  const chatMessages = useStore((s) => s.chatMessages);
  const streamText = useStore((s) => s.streamText);
  const streamTool = useStore((s) => s.streamTool);
  const chatBusy = useStore((s) => s.chatBusy);
  const chatError = useStore((s) => s.chatError);
  const sendChat = useStore((s) => s.sendChat);
  const fetchGrade = useStore((s) => s.fetchGrade);
  const health = useStore((s) => s.health);

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const claudeUp = health === null || health.claude; // optimistic until health arrives
  const analysisState = analysisAvailability(health);
  const katagoReady = analysisState === 'ready';

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatMessages.length, streamText, streamTool]);

  if (!detail) return null;

  const send = (text: string) => {
    if (!text.trim() || chatBusy || !claudeUp) return;
    setDraft('');
    void sendChat(text);
  };

  const suggestMove = () => {
    setQuery({ overlay: 'suggestions' });
    send('What would be a good move here, and why?');
  };

  const gradeLastMove = () => {
    const last = lastHumanBoardMove(detail);
    if (last) void fetchGrade(last.moveNumber);
    send('How was my last move?');
  };

  const { stable, tail } = splitStreamTail(streamText);
  const streaming = chatBusy && (streamText.length > 0 || streamTool !== null);

  return (
    <div className="coach-chat">
      <div className="chat-scroll" ref={scrollRef} aria-live="polite">
        {chatMessages.length === 0 && !streaming && (
          <div className="coach-empty">
            <span className="coach-mark" aria-hidden="true">✦</span>
            <div>
              <h3>Study the position together.</h3>
              <p>
                The coach sees this board and its move history. Start with a prompt below,
                or ask about a shape that feels unclear.
              </p>
            </div>
          </div>
        )}
        {chatMessages.map((m) => (
          <Message key={m.id} msg={m} />
        ))}
        {streaming && (
          <div className="chat-msg chat-assistant chat-streaming" role="status" aria-label="Coach is responding">
            {stable && <ReactMarkdown>{stable}</ReactMarkdown>}
            {tail && <p className="stream-tail">{tail}</p>}
            {streamTool && (
              <span className="tool-chip">{TOOL_LABEL[streamTool] ?? `${streamTool}…`}</span>
            )}
            {!streamText && !streamTool && <span className="tool-chip">Thinking…</span>}
          </div>
        )}
        {chatError && (
          <p className="chat-error" role="alert">
            {chatError}
          </p>
        )}
      </div>

      {cursor !== null && (
        <p className="chat-cursor-note">
          Coach sees the board at move {cursor} of {detail.moves.length}.
        </p>
      )}

      <div className="chip-row" role="group" aria-label="Quick coach actions">
        <button
          type="button"
          className="chip"
          disabled={chatBusy || !claudeUp || !katagoReady}
          title={katagoReady ? undefined : 'KataGo is not ready yet'}
          onClick={suggestMove}
        >
          Suggest a move
        </button>
        <button
          type="button"
          className="chip"
          disabled={chatBusy || !claudeUp || !katagoReady || lastHumanBoardMove(detail) === null}
          title={katagoReady ? undefined : 'KataGo is not ready yet'}
          onClick={gradeLastMove}
        >
          How was my last move?
        </button>
        <button
          type="button"
          className="chip"
          disabled={chatBusy || !claudeUp}
          onClick={() => send('Explain the current position.')}
        >
          Explain the position
        </button>
        <button
          type="button"
          className="chip"
          disabled={chatBusy || !claudeUp}
          onClick={() => send('Who is winning right now, and by roughly how much?')}
        >
          Who&rsquo;s winning?
        </button>
      </div>

      {claudeUp && !katagoReady && (
        <p className="chat-availability" role="status">
          Position explanations are ready. Move suggestions and grading will unlock when
          KataGo {analysisState === 'checking' ? 'finishes its status check' : analysisState === 'warming' ? 'finishes warming up' : 'comes back online'}.
        </p>
      )}

      {!claudeUp ? (
        <p className="chat-disabled-note">
          The coach is currently offline. Your game and move history remain available.
        </p>
      ) : (
        <form
          className="chat-input-row"
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
        >
          <textarea
            className="chat-input"
            rows={2}
            placeholder="Ask the coach…"
            value={draft}
            disabled={chatBusy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(draft);
              }
            }}
            aria-label="Message the coach"
          />
          <button type="submit" className="btn btn-accent chat-send" disabled={chatBusy || !draft.trim()}>
            {chatBusy ? 'Replying…' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
}
